import { useEffect, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { api } from '../../shell/bridge';
import { voteDecision, type DecisionView, type Role } from '../../lib/api';
import { DECISION_VERBS } from '../../../../shared/decisions';
import { createDateIdea, dateCostLabel, dateModeLabel } from '../../lib/dateIdeas';
/* One-tap Today (hSgJtaF6deWm): the nudge card's quick-picks are four of the
   hand-curated library, so the titles/notes come from the same source the
   concierge's suggestions use — one library, no copy drift. */
import { DATE_TEMPLATES, type DateTemplate } from '../../../../shared/date-templates';
import { parseDateNudge } from '../../../../shared/date-nudge';
/* Founder email 2026-10-27: the thread's reveal card and Reveal history share
   one presentation component rather than two lookalikes that drift. */
import { RevealPair } from '../../components/RevealPair';
import { Overlay } from '../../ui/Overlay';
import { markTodayCardVisible, logTodayAction } from '../../lib/todayMetrics';
import { reportActionFailure } from '../../lib/actionFeedback';
import { Bubble } from './message';
import { QUESTION_GLYPH } from './glyphs';

/**
 * Request card (card 2eueFMxFylie) — renderer only, the state machine lives
 * in the request routes. The payload rides in the text column as JSON, so
 * parsing is defensive: anything that is not our shape renders as a plain
 * bubble rather than crashing the whole message list.
 */
interface RequestCardPayload {
  requestId: string;
  action: 'posted' | 'accepted' | 'countered' | 'declined';
  requestText: string;
}

export function parseRequestCard(raw: string): RequestCardPayload | null {
  if (typeof raw !== 'string') return null;
  try {
    const p = JSON.parse(raw) as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return null;
    const { requestId, action, requestText } = p;
    if (typeof requestId !== 'string' || requestId.length === 0) return null;
    if (action !== 'posted' && action !== 'accepted' && action !== 'countered' && action !== 'declined') return null;
    if (typeof requestText !== 'string') return null;
    return { requestId, action, requestText };
  } catch {
    return null;
  }
}

export function RequestCard({ seq, text, mine, partnerName, role }: {
  seq: number;
  text: string;
  mine: boolean;
  partnerName: string;
  role: Role | null;
}) {
  // Hooks first — the malformed-payload early return below must not change
  // the hook order between renders.
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const payload = parseRequestCard(text);
  // One-tap Today (hSgJtaF6deWm): start this card's interaction clock on
  // mount so the accept/decline below can report how long the card sat
  // painted before the tap actually completed.
  useEffect(() => {
    markTodayCardVisible('request', seq);
  }, [seq]);

  if (!payload) return <Bubble text={text} mine={mine} role={role} />;

  const transition = async (action: 'accept' | 'decline') => {
    if (busy) return;
    setBusy(action);
    try {
      // Existing route, existing helper — the socket fan-out delivers the
      // next card, so the buttons only have to survive their own flight.
      await api(`/api/requests/${payload.requestId}/transition`, { body: { action } });
      logTodayAction('request', seq, action);
    } catch (e) {
      console.error('[Pear] request transition failed', e);
      reportActionFailure('update the request');
    } finally {
      setBusy(null);
    }
  };

  // accepted | countered | declined: one quiet line, no buttons.
  if (payload.action !== 'posted') {
    const who = mine ? 'You' : partnerName;
    const verb = payload.action === 'accepted' ? 'accepted' : payload.action === 'countered' ? 'countered' : 'declined';
    return (
      <div
        className="pear-request-card is-status"
        key={`request-${seq}`}
        data-request-card-seq={seq}
        data-request-action={payload.action}
      >
        <p className="pear-request-status">{`${who} ${verb}: ${payload.requestText}`}</p>
      </div>
    );
  }

  // posted: the ask, plus the inline answer — but never on your own ask.
  const showActions = !mine;
  return (
    <div className="pear-request-card" key={`request-${seq}`} data-request-card-seq={seq} data-request-action="posted">
      <div className="pear-request-body">
        <span className="pear-request-label">
          {mine ? 'You asked' : `Request from ${partnerName}`}
        </span>
        <p className="pear-request-text">{payload.requestText}</p>
        {showActions && (
          <div className="pear-request-actions">
            <button
              type="button"
              className="pear-btn pear-btn-mustard"
              data-request-act="accept"
              disabled={busy !== null}
              onClick={() => void transition('accept')}
            >
              {busy === 'accept' ? 'Accepting…' : 'Accept'}
            </button>
            <button
              type="button"
              className="pear-btn"
              data-request-act="decline"
              disabled={busy !== null}
              onClick={() => void transition('decline')}
            >
              {busy === 'decline' ? 'Declining…' : 'Decline'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Question card (card jinkfGNSDI4j) — today's question as a typed Thread card.
 * The backend writes ONE message per question and rewrites it in place when
 * both have answered, so this just draws the payload it receives: pending
 * means answer inline and show only WHO has answered (never their text);
 * revealed puts both lines in place with a way back to the history in Us.
 * The payload rides in the text column, so the parse is defensive.
 */
interface QuestionCardPayload {
  questionId: string;
  question: string;
  state: 'pending' | 'revealed';
  answeredBy: string[];
  answers?: { memberId: string; text: string; late?: boolean }[];
}

export function parseQuestionCard(raw: string): QuestionCardPayload | null {
  if (typeof raw !== 'string') return null;
  try {
    const p = JSON.parse(raw) as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return null;
    const { questionId, question, state, answeredBy } = p;
    if (typeof questionId !== 'string' || questionId.length === 0) return null;
    if (typeof question !== 'string') return null;
    if (state !== 'pending' && state !== 'revealed') return null;
    const by = Array.isArray(answeredBy)
      ? answeredBy.filter((m): m is string => typeof m === 'string')
      : [];
    const rawAnswers = (p as { answers?: unknown }).answers;
    const answers = Array.isArray(rawAnswers)
      ? rawAnswers.filter((a): a is { memberId: string; text: string; late?: boolean } =>
          !!a && typeof a === 'object' &&
          typeof (a as { memberId?: unknown }).memberId === 'string' &&
          typeof (a as { text?: unknown }).text === 'string')
      : undefined;
    return { questionId, question, state, answeredBy: by, answers };
  } catch {
    return null;
  }
}

/**
 * A decision as it sits in the stream (parent VcBi3nr9GjBj, child B
 * HMzNgJETH7HE). It rewrites in place: one element keyed on the message seq,
 * drawn from the decisions row rather than from any text the server sent back,
 * so pending → settled is a re-render of the SAME card. A second card was the
 * failure mode this shape exists to avoid.
 *
 * The seal is inherited, not re-implemented — `votes` only ever holds the
 * partner's pick once both of you have voted, because the slice removes it at
 * the source. So the pending branch below cannot leak the partner's choice
 * even if it wanted to: the choice is not in the payload it is given.
 */
export function DecisionCard({ seq, decision, myMemberId, nameFor, partnerName, onOpenDetail }: {
  seq: number;
  decision: DecisionView;
  myMemberId: string | null;
  nameFor?: (memberId: string | null | undefined) => string | null;
  partnerName?: string | null;
  /* Card es19s3sd4gJa: the whole card is the tap target for the decision's
     own detail. It is a callback rather than a navigation so the thread does
     not unmount — back returns to the stream at the same scroll offset. */
  onOpenDetail?: (decisionId: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const label = (memberId: string) =>
    memberId === myMemberId ? 'You' : (nameFor?.(memberId) ?? partnerName ?? 'Your partner');

  /* The seal already did the work: `votes` carries only the members who have
     actually voted, and the partner's choice is stripped from it while the
     decision is pending. So "has my partner voted" is readable here and
     "what did they pick" is not — the card cannot leak what it is not given. */
  const votes = decision.votes ?? [];
  const myVote = votes.find(v => v.mine)?.choice;
  const partnerVoted = votes.some(v => !v.mine);
  const settled = decision.state === 'revealed' || decision.state === 'tied';

  const vote = async (choice: 'a' | 'b') => {
    if (busy || myVote) return;
    setBusy(true);
    setError('');
    try {
      await voteDecision(decision.decisionId, choice);
    } catch (err) {
      console.error('[Pear] decision vote failed', err);
      setError('Your vote did not save');
    } finally {
      setBusy(false);
    }
  };

  const openDetail = () => {
    if (onOpenDetail) onOpenDetail(decision.decisionId);
  };
  /* A tap anywhere on the card opens the detail, EXCEPT on the vote options
     inside it — those stay their own buttons, so the card's handler skips any
     click that landed inside one. */
  const onCardClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!onOpenDetail) return;
    if (e.target instanceof Element && e.target.closest('button, a, input, textarea, select')) return;
    openDetail();
  };
  const onCardKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!onOpenDetail) return;
    /* The vote buttons live inside this card, so their own Space/Enter must
       not bubble up and open the sheet as well. */
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetail();
    }
  };

  return (
    <div
      className={`pear-thread-card pear-decision-card${onOpenDetail ? ' is-openable' : ''}`}
      key={`decision-card-${seq}`}
      data-test="decision-card"
      data-decision-id={decision.decisionId}
      data-decision-state={decision.state}
      {...(onOpenDetail
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': `Open decision: ${decision.title}`,
            'aria-haspopup': 'dialog' as const,
            onClick: onCardClick,
            onKeyDown: onCardKeyDown,
          }
        : {})}
    >
      <span className="pear-card-emoji" aria-hidden="true">⚖️</span>
      <div className="pear-card-body">
        <p className="pear-decision-label">Decision</p>
        <p className="pear-decision-text">{decision.title}</p>

        {settled ? (
          /* Both in: the two picks and what they landed on, in the order the
             outcome names them. A tie says so — it is a result, not a failure
             to reach one. */
          <div className="pear-decision-result" data-test="decision-result">
            {votes.map(v => (
              <p className="pear-decision-pick" key={v.memberId}>
                {label(v.memberId)}: {v.choice === 'a' ? decision.options.a : decision.options.b}
              </p>
            ))}
            <p className="pear-decision-outcome">{decision.outcomeLine}</p>
          </div>
        ) : (
          <div className="pear-decision-options" data-test="decision-options">
            {/* The two options with the locked card verbs (parent card): the
                first is the one you are locking in, the second is the one
                you are happy to leave to them. */}
            {(['a', 'b'] as const).map(choice => {
              const text = decision.options[choice];
              return (
                <button
                  type="button"
                  className={`pear-decision-option${myVote === choice ? ' is-mine' : ''}`}
                  data-test={`decision-option-${choice}`}
                  disabled={busy || !!myVote}
                  onClick={() => void vote(choice)}
                >
                  {text}
                  <span className="pear-decision-verb">{DECISION_VERBS[choice]}</span>
                </button>
              );
            })}
            {/* Who is in, said plainly. Never which option — that is the part
                the seal withholds until both of you are here. */}
            <p className="pear-decision-waiting" data-test="decision-waiting">
              {myVote && partnerVoted
                ? 'Both votes are in.'
                : myVote
                  ? 'You have voted — waiting on them.'
                  : partnerVoted
                    ? 'They have voted.'
                    : 'Waiting on both of you.'}
            </p>
            {error && <p className="pear-decision-error">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

/** The line a decision is in, in words. Mirrors the Decisions rail's own
 *  `stateLine` (Life/Decisions.tsx) — the thread sheet and the owning screen
 *  must not describe the same decision two different ways. */
function decisionStateLine(
  decision: DecisionView,
  label: (memberId: string) => string,
): string {
  if (decision.state === 'tied') return 'You picked differently — it is on the Plan agenda.';
  if (decision.state === 'revealed') return decision.outcomeLine ?? 'Both of you picked.';
  if (decision.stalled) return 'Still undecided — it is on the Plan agenda.';
  const voted = (decision.votes ?? []).map(v => label(v.memberId));
  return voted.length === 0
    ? 'Neither of you has picked yet.'
    : `${voted.join(' and ')} picked — the other is still open.`;
}

/**
 * The decision, full size (card es19s3sd4gJa).
 *
 * The thread card is a summary: two options and a waiting line, sized for the
 * stream. Tapping it opens this — the same entry the owning screen (Life >
 * Requests) draws, with the question, both options and their verbs, who is
 * in, and the live vote. It is an overlay rather than a route for one reason
 * the card states: Back has to return to the SAME scroll position in the
 * thread, and switching tabs unmounts the stream.
 *
 * The payload is the same sealed `DecisionView` the card was given, so this
 * sheet cannot reveal a pick the partner has not made yet — the seal happens
 * server-side, before either component sees the row.
 */
export function DecisionDetail({ decision, myMemberId, nameFor, partnerName, onClose }: {
  decision: DecisionView;
  myMemberId: string | null;
  nameFor?: (memberId: string | null | undefined) => string | null;
  partnerName?: string | null;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const label = (memberId: string) =>
    memberId === myMemberId ? 'You' : (nameFor?.(memberId) ?? partnerName ?? 'Your partner');

  const votes = decision.votes ?? [];
  const myVote = votes.find(v => v.mine)?.choice;
  const settled = decision.state === 'revealed' || decision.state === 'tied';

  const vote = async (choice: 'a' | 'b') => {
    if (busy || myVote) return;
    setBusy(true);
    setError('');
    try {
      await voteDecision(decision.decisionId, choice);
    } catch (err) {
      console.error('[Pear] decision vote failed', err);
      setError('Your vote did not save');
      reportActionFailure('Your vote did not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay
      open
      onClose={onClose}
      className="pear-decision-overlay"
      panelClassName="pear-decision-detail"
      testId="decision-detail"
      label={`Decision: ${decision.title}`}
      restoreFocusTo={`[data-decision-id="${decision.decisionId}"]`}
      restoreFocusPreventScroll
    >
      <p className="pear-decision-label">Decision</p>
      <h2 className="pear-decision-detail-title" data-test="decision-detail-title">
        {decision.title}
      </h2>
      <p className="pear-decision-detail-state" data-test="decision-detail-state">
        {decisionStateLine(decision, label)}
      </p>

      {settled ? (
        <div className="pear-decision-result" data-test="decision-detail-result">
          {votes.map(v => (
            <p className="pear-decision-pick" key={v.memberId}>
              {label(v.memberId)}: {v.choice === 'a' ? decision.options.a : decision.options.b}
            </p>
          ))}
          {decision.outcomeLine && (
            <p className="pear-decision-outcome">{decision.outcomeLine}</p>
          )}
        </div>
      ) : (
        <div className="pear-decision-options" data-test="decision-detail-options">
          {(['a', 'b'] as const).map(choice => {
            const text = decision.options[choice];
            return (
              <button
                type="button"
                key={choice}
                className={`pear-decision-option${myVote === choice ? ' is-mine' : ''}`}
                data-test={`decision-detail-option-${choice}`}
                disabled={busy || !!myVote}
                onClick={() => void vote(choice)}
              >
                {text}
                <span className="pear-decision-verb">{DECISION_VERBS[choice]}</span>
              </button>
            );
          })}
          <p className="pear-decision-waiting">
            {myVote && votes.some(v => !v.mine)
              ? 'Both votes are in.'
              : myVote
                ? 'You have voted — waiting on them.'
                : votes.some(v => !v.mine)
                  ? 'They have voted.'
                  : 'Waiting on both of you.'}
          </p>
          {error && <p className="pear-decision-error" role="alert">{error}</p>}
        </div>
      )}

      <button type="button" className="pear-btn pear-decision-detail-back" onClick={onClose}>
        Back to thread
      </button>
    </Overlay>
  );
}

export function QuestionCard({ seq, text, myMemberId, nameFor, partnerName, onOpenUs }: {
  seq: number;
  text: string;
  myMemberId: string | null;
  nameFor?: (memberId: string | null | undefined) => string | null;
  /* Card DfhelObKj9iP (Jonathan 2026-10-30): the answer marker under the card
     names her instead of saying "Your partner" — this card is only ever drawn
     in a pair, so there is a name to use. */
  partnerName?: string | null;
  onOpenUs?: () => void;
}) {
  // Hooks first — the malformed-payload early return below must not change
  // the hook order between renders (same rule as RequestCard).
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const payload = parseQuestionCard(text);
  // One-tap Today (hSgJtaF6deWm): start the interaction clock on mount.
  useEffect(() => {
    markTodayCardVisible('question', seq);
  }, [seq]);

  if (!payload) return <Bubble text={text} mine={false} role={null} />;

  const iAnswered = myMemberId ? payload.answeredBy.includes(myMemberId) : false;
  const partnerAnswered = payload.answeredBy.some(id => id !== myMemberId);

  const label = (memberId: string) =>
    memberId === myMemberId ? 'You' : nameFor?.(memberId) ?? 'Your partner';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = draft.trim();
    if (!value || busy) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/questions/${payload.questionId}/answer`, { body: { text: value } });
      setDraft('');
      logTodayAction('question', seq, 'answer');
      // Solo accumulation (card Pn1wrppZMWEr): this answer may be the one
      // that tops the sealed stack — tell it to re-count.
      window.dispatchEvent(new CustomEvent('pear:questions-changed'));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Your Answer Did Not Save');
      console.error('[Pear] question answer failed', err);
      reportActionFailure('save your answer');
    } finally {
      setBusy(false);
    }
  };

  if (payload.state === 'revealed') {
    const answers = payload.answers ?? [];
    /* A reveal nobody is in is not a card worth drawing (2026-10-17 email):
       zero answers means the day passed with nothing said, and the empty
       "Neither of you answered this one" card read as the question haunting
       the stream. It disappears — the next question lands in its place. */
    if (answers.length === 0) return null;
    /* A one-sided reveal used to read as if both were in. It now says who
       answered and that their response is what is being revealed — the
       partner's absence is stated, never implied to be a presence.
       `label()` maps the reader to the bare word "You", which does not carry a
       verb ("You has answered"), so branch on first person and name the
       partner the second person way (found by _pear_reveal_probe.mjs). */
    const answerer = answers.length === 1
      ? (answers[0].memberId === myMemberId
          ? 'You have answered'
          : `${label(answers[0].memberId)} has answered`)
      : null;
    return (
      <div
        className="pear-thread-card pear-question-card is-revealed"
        data-question-card-seq={seq}
        data-question-state="revealed"
        /* Today's questionId is exposed so a probe can anchor to THIS card:
           Thread.tsx keeps the highest-seq card, and a dev-seeded past
           question (walk fixture) outranks it by seq. Without this the probe
           measures the fixture and reports today's card as broken. */
        data-question-id={payload.questionId}
      >
        <span className="pear-card-emoji" aria-hidden="true">{QUESTION_GLYPH}</span>
        {/* Body wrapper aligns the question card's column with the game
            card's .pear-game-body, so the two cards' text starts on the same
            row. Indentation of the children is left alone on purpose — this
            commit is the shell, not a whitespace sweep. */}
        <div className="pear-card-body">
        <p className="pear-question-label">Today's question</p>
        <p className="pear-question-text">{payload.question}</p>
        {/* No confetti, just motion (Jonathan 2026-10-30, second call, card
            ZXJan7hSZq2G). The question card keeps its own reveal transition;
            the particle layer belongs to a shared game win only, not to this
            reveal (card psHEfEkK9cT2). */}
        {/* Who is actually in the reveal, said out loud (2026-10-17 email).
            Both answered reads as the ceremony; one answered names that
            person and reveals only their response. */}
        {answerer && (
          <p className="pear-question-scope" data-question-scope="one">
            {answerer} — revealing their response.
          </p>
        )}
        <div className="pear-question-answers">
          {/* Founder email 2026-10-27: the reveal card attached to the thread
              uses the SAME implementation as the full reveal history — the
              shared RevealPair (bounded columns, pinned labels, measured
              clamp). It used to stack two loose answer blocks here, so the
              same pair of answers read differently depending on where you
              looked. */}
          <RevealPair
            answers={answers}
            order={myMemberId ? [myMemberId, ...answers.map(a => a.memberId).filter(id => id !== myMemberId)] : answers.map(a => a.memberId)}
            nameFor={label}
            testId="thread-reveal"
          />
        </div>
        {/* FOUNDING CHANGE (email 2026-10-21): the thread no longer hosts its
            own question text entry. One answer pill routes to the dedicated
            question entry in Us → Questions, so the answer is written in one
            place and the home thread stays a stream. Late answering still
            works — the Questions panel keeps its own late composer. */}
        {!iAnswered && onOpenUs && (
          <button
            type="button"
            className="pear-btn pear-question-submit pear-question-answer-btn"
            data-test="question-answer-btn"
            onClick={onOpenUs}
          >
            Answer in Questions →
          </button>
        )}
        {onOpenUs && (
          <button type="button" className="pear-question-link" onClick={onOpenUs}>
            See history in Us
          </button>
        )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="pear-thread-card pear-question-card"
      data-question-card-seq={seq}
      data-question-state="pending"
    >
      <span className="pear-card-emoji" aria-hidden="true">{QUESTION_GLYPH}</span>
      <div className="pear-card-body">
      <p className="pear-question-label">Today's question</p>
      <p className="pear-question-text">{payload.question}</p>
      <div className="pear-question-markers">
        <span className={`pear-question-marker${iAnswered ? ' is-answered' : ''}`}>
          You: {iAnswered ? 'answered' : 'not yet'}
        </span>
        <span className={`pear-question-marker${partnerAnswered ? ' is-answered' : ''}`}>
          {partnerName ?? 'Your partner'}: {partnerAnswered ? 'answered' : 'not yet'}
        </span>
      </div>
      {iAnswered ? (
        <p className="pear-question-waiting">Locked in — it opens when they answer, or after a day.</p>
      ) : onOpenUs ? (
        /* Same pill as the revealed branch (email 2026-10-21): the thread
           carries no question text entry at all now. */
        <button
          type="button"
          className="pear-btn pear-question-submit pear-question-answer-btn"
          data-test="question-answer-btn"
          onClick={onOpenUs}
        >
          Answer in Questions →
        </button>
      ) : (
        <form className="pear-question-form" onSubmit={submit}>
          <input
            className="pear-question-input"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Your answer…"
            maxLength={400}
            aria-label="Your answer to today's question"
            disabled={busy}
          />
          <button
            type="submit"
            className="pear-btn pear-question-submit"
            disabled={busy || draft.trim().length === 0}
          >
            {busy ? 'Saving…' : 'Answer'}
          </button>
        </form>
      )}
      {error && <p className="pear-question-error">{error}</p>}
      </div>
    </div>
  );
}

/**
 * Locked-date card (card ZdYBVP203b8a, PRD §6.3) — "You're both in".
 *
 * The server posts exactly one of these, on the affirm that closes the
 * round, so there is no pending state to draw and no answer that could
 * ride it early: a date card only exists once the date is LOCKED. The
 * payload lives in the text column like every other typed card, so the
 * parse is defensive — a malformed payload degrades to a plain bubble.
 */
interface DateCardPayload {
  ideaId: string;
  title: string;
  placeName: string | null;
  mode: string;
  costBand: string;
  energy: string;
  calendarStatus: string;
}

function parseDateCard(raw: string): DateCardPayload | null {
  if (typeof raw !== 'string') return null;
  try {
    const p = JSON.parse(raw) as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return null;
    const { ideaId, title, placeName, mode, costBand, energy, calendarStatus } = p;
    if (typeof ideaId !== 'string' || ideaId.length === 0) return null;
    if (typeof title !== 'string' || title.length === 0) return null;
    return {
      ideaId,
      title,
      placeName: typeof placeName === 'string' && placeName ? placeName : null,
      mode: typeof mode === 'string' ? mode : 'stay_in',
      costBand: typeof costBand === 'string' ? costBand : '$$',
      energy: typeof energy === 'string' ? energy : 'med',
      calendarStatus: typeof calendarStatus === 'string' ? calendarStatus : 'none',
    };
  } catch {
    return null;
  }
}


export function DateCard({ seq, text, onOpenUs }: {
  seq: number;
  text: string;
  onOpenUs?: () => void;
}) {
  // Hooks first — the malformed-payload early return must not change hook
  // order between renders (same rule as RequestCard and QuestionCard).
  const [busy, setBusy] = useState(false);
  const [rainedOut, setRainedOut] = useState(false);
  // One-tap Today (hSgJtaF6deWm): a locked date with no time used to tell
  // you to go to Us → Dates to set one — a navigation for an action this card
  // can take itself. The picker below sets it here, and `calStatus` lets the
  // card flip its own calendar line in place instead of waiting for a reload
  // it never triggers.
  const [calStatus, setCalStatus] = useState<string | null>(null);
  const [whenValue, setWhenValue] = useState('');
  const payload = parseDateCard(text);
  // Start this card's interaction clock on mount (see lib/todayMetrics.ts).
  useEffect(() => {
    markTodayCardVisible('date', seq);
  }, [seq]);

  if (!payload) return <Bubble text={text} mine={false} role={null} />;

  const meta = [dateCostLabel(payload.costBand), dateModeLabel(payload.mode)]
    .filter(Boolean)
    .join(' · ');

  /** PRD §6.2c — a raincheck reschedules without blame, so the copy never
   *  asks why. One tap; the card closes itself rather than re-fetching. */
  const raincheck = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api(`/api/dates/${payload.ideaId}/outcome`, { body: { outcome: 'raincheck' } });
      setRainedOut(true);
      logTodayAction('date', seq, 'raincheck');
    } catch (err) {
      console.error('[Pear] date raincheck failed', err);
      reportActionFailure('reschedule the date');
    } finally {
      setBusy(false);
    }
  };

  /** Set (or move) the time from the card itself. Same route Us → Dates
   *  calls; only the surface is new. datetime-local sends local wall time,
   *  so the epoch-ms conversion happens here — once, matching Dates.tsx. */
  const setWhen = async () => {
    const ms = new Date(whenValue).getTime();
    if (busy || !Number.isFinite(ms)) return;
    setBusy(true);
    try {
      const view = await api<{ calendarStatus?: string }>(`/api/dates/${payload.ideaId}/when`, {
        body: { when: ms },
      });
      // Without Google connected the write stays unsent, and saying
      // "going on the calendar" would be a lie — take the server's word.
      setCalStatus(view?.calendarStatus ?? 'needs_time');
      logTodayAction('date', seq, 'set_time');
    } catch (err) {
      console.error('[Pear] date set time failed', err);
      reportActionFailure('set the time');
    } finally {
      setBusy(false);
    }
  };

  const status = calStatus ?? payload.calendarStatus;
  const needsTime = status === 'needs_time' && !rainedOut;

  return (
    <div className="pear-date-card" data-date-card-seq={seq} data-date-idea={payload.ideaId}>
      <p className="pear-date-kicker">You're both in</p>
      <p className="pear-date-title">{payload.title}</p>
      {payload.placeName && <p className="pear-date-place">{payload.placeName}</p>}
      <p className="pear-date-meta">{meta}</p>
      <p className="pear-date-cal" data-calendar={status}>
        {status === 'written'
          ? 'On the calendar'
          : status === 'queued'
            ? 'Going on the calendar'
            : needsTime
              ? 'No time yet — pick one below and we will put it on the calendar'
              : 'Not on the calendar yet — connect Google and we will add it'}
      </p>
      {needsTime && (
        /* One-tap Today (hSgJtaF6deWm): the action stays on the card. */
        <div className="pear-date-when" data-testid="date-set-when">
          <input
            type="datetime-local"
            className="pear-date-when-input"
            aria-label="When is this date"
            value={whenValue}
            onChange={e => setWhenValue(e.target.value)}
            disabled={busy}
          />
          <button
            type="button"
            className="pear-btn pear-btn-mustard pear-date-when-set"
            disabled={busy || !whenValue}
            onClick={() => void setWhen()}
          >
            {busy ? 'Saving…' : 'Set time'}
          </button>
        </div>
      )}
      {rainedOut ? (
        <p className="pear-date-done" data-testid="date-rained-out">
          Rained out — we will find a better time.
        </p>
      ) : (
        <div className="pear-date-actions">
          <button type="button" className="pear-btn pear-btn-blush pear-date-raincheck" onClick={() => void raincheck()} disabled={busy}>
            {busy ? 'Saving…' : 'Raincheck'}
          </button>
          {onOpenUs && (
            <button type="button" className="pear-date-link" onClick={onOpenUs}>
              Open in Us →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Cadence nudge card (card yejoF8uTVxyf, PRD §6.3/§9): "Agent suggestion
 * nudges arrive in Thread as deep links into the card, not as a chat message
 * to reply to."
 *
 * One-tap Today (hSgJtaF6deWm): the deep link alone failed the card's
 * acceptance criterion — the nudge's core action is "pick something for this
 * window", and sending you to Us → Dates to do it made it a
 * navigation-required action. So the nudge now carries four quick-picks that
 * capture a date idea here, in place, and the trip to Us → Dates becomes the
 * way you refine it, not the way you start it.
 *
 * The four are drawn from the hand-curated library (shared/date-templates.ts)
 * and chosen for a nudge's context: every one is low-effort or free, because
 * a couple being nudged on cadence does not need a $$$ project suggested at
 * it. A malformed payload degrades to a plain bubble, exactly like the cards
 * above.
 */
const NUDGE_QUICK_PICK_KEYS = [
  'game-card-tournament',
  'cook-taco-bar',
  'game-trivia-night',
  'movie-double-feature',
] as const;

export function DateNudgeCard({ seq, text, onOpenDates }: {
  seq: number;
  text: string;
  onOpenDates?: () => void;
}) {
  // Hooks first — the malformed-payload early return must not change hook
  // order between renders (same rule as the cards above).
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const payload = parseDateNudge(text);
  // Start this card's interaction clock on mount (see lib/todayMetrics.ts).
  useEffect(() => {
    markTodayCardVisible('nudge', seq);
  }, [seq]);

  if (!payload) return <Bubble text={text} mine={false} role={null} />;

  const picks = NUDGE_QUICK_PICK_KEYS
    .map(key => DATE_TEMPLATES.find(t => t.key === key))
    .filter((t): t is DateTemplate => !!t);

  /** Capture the chosen template as a real date idea — the same POST the
   *  Dates panel's composer sends, so a quick-pick is a capture, not a
   *  separate kind of object. The card confirms in place; both of you see it
   *  in Us → Dates on the next load. */
  const quickPick = async (t: DateTemplate) => {
    if (busyKey) return;
    setBusyKey(t.key);
    try {
      await createDateIdea({
        title: t.title,
        note: t.note,
        mode: t.mode,
        costBand: t.costBand,
        energy: t.energy,
        source: 'nudge',
      });
      setPicked(t.title);
      logTodayAction('nudge', seq, 'quick_pick');
    } catch (err) {
      console.error('[Pear] nudge quick-pick failed', err);
      reportActionFailure('add that to your plan');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div
      className="pear-agent-card pear-date-nudge"
      data-date-nudge-seq={seq}
      data-date-nudge-window={payload.window}
    >
      <span className="pear-agent-dot" />
      <div className="pear-date-nudge-body">
        <p className="pear-agent-copy">{payload.copy}</p>
        {picked ? (
          /* One-tap Today (hSgJtaF6deWm): the action completed on the card,
             so the card says so rather than staying an unanswered prompt. */
          <p className="pear-date-nudge-picked" data-testid="date-nudge-picked">
            Added: {picked} — find it in Us → Dates.
          </p>
        ) : (
          <div className="pear-date-nudge-picks" role="group" aria-label="Quick date picks">
            {picks.map(t => (
              <button
                key={t.key}
                type="button"
                className="pear-btn pear-date-nudge-pick"
                data-nudge-pick={t.key}
                disabled={busyKey !== null}
                onClick={() => void quickPick(t)}
              >
                {busyKey === t.key ? 'Adding…' : t.title}
              </button>
            ))}
          </div>
        )}
        {onOpenDates && (
          <button
            type="button"
            className="pear-date-link pear-date-nudge-open"
            aria-label="Open Dates in the Us tab"
            onClick={onOpenDates}
          >
            See all date ideas →
          </button>
        )}
      </div>
    </div>
  );
}
